import React, { FC, useCallback, useContext, useEffect, useRef, useState } from 'react'

import { BraintreeContext } from '@Domains/Checkout/Providers/BraintreeContextProvider'
import { PaymentIcon } from '@Domains/Checkout/Steps/Payment/Components/PaymentIconFactory/PaymentIconFactory'
import { CheckoutFlowContext } from '@Domains/CheckoutV2/Features/CheckoutSteps/Contexts/CheckoutFlowContext'
import { CheckoutFlowStep } from '@Domains/CheckoutV2/Features/CheckoutSteps/Contexts/typings/CheckoutFlowTypes'
import {
  useBraintreeCheckoutResponse,
} from '@Domains/CheckoutV2/Features/CheckoutSteps/Steps/CheckoutPaymentStep/Components/ProviderComponents/Braintree/Hooks/useBraintreeCheckoutResponse'
import { CheckoutPaymentComponentProps } from '@Domains/CheckoutV2/Features/CheckoutSteps/Steps/CheckoutPaymentStep/Hooks/usePaymentComponentFactory'
import { Spinner } from '@Shared/Components/Spinner/Spinner'

import styles from './BraintreeVenmo.module.scss'

export const BraintreeVenmoButton: FC<CheckoutPaymentComponentProps> = ({ grandTotal, option, submitPayment }) => {
  const { handleBraintreePaymentSubmitResponse } = useBraintreeCheckoutResponse()
  const { stepValidations } = useContext(CheckoutFlowContext)
  const { authorization, braintreeClient, braintreeInstance } = useContext(BraintreeContext)
  const [disabled, setDisabled] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [venmo, setVenmo] = useState<any | null>(null)
  const [deviceData, setDeviceData] = useState<unknown>(null)
  const grandTotalRef = useRef(grandTotal)
  const allValid
      = stepValidations[CheckoutFlowStep.ADDRESS]
      && stepValidations[CheckoutFlowStep.SHIPPING]

  useEffect(() => {
    grandTotalRef.current = grandTotal
  }, [grandTotal])

  // https://developer.paypal.com/braintree/docs/guides/venmo/client-side/javascript/v3/#collect-device-data
  const createDeviceDataCollection = useCallback(() => {
    if (!mounted && !!braintreeInstance?.dataCollector && !deviceData && !!braintreeClient) {
      braintreeInstance.dataCollector.create({
        client: braintreeClient,
        paypal: true,
      }, (dataCollectorErr, dataCollectorInstance) => {
        if (dataCollectorErr) {
          console.warn('Venmo dataCollectorErr: ', dataCollectorErr)
        }
        setDeviceData(dataCollectorInstance?.deviceData)
      })
    }
  }, [braintreeClient, braintreeInstance?.dataCollector, deviceData, mounted])

  useEffect(() => {
    createDeviceDataCollection()
    if (!mounted && !!braintreeInstance?.venmo && !!deviceData) {
      setMounted(true)
      braintreeInstance.venmo.create({
        client: braintreeClient,
        authorization,
        allowDesktop: true,
        allowWebviews: false,
        totalAmount: grandTotal?.value?.plain,
        mobileWebFallBack: true,
        allowDesktopWebLogin: true,
        paymentMethodUsage: 'single_use',
        allowNewBrowserTab: false,
      }, (venmoErr, venmoInstance: any) => {
        if (venmoErr) {
          console.warn('Error creating Braintree venmo Checkout:', venmoErr)
          return
        }
        if (!venmoInstance?.isBrowserSupported()) {
          console.warn('Browser does not support Venmo')
          return
        }
        setVenmo(venmoInstance)
      })
    }
  }, [authorization, braintreeClient, braintreeInstance, mounted, deviceData, createDeviceDataCollection, grandTotal])

  return (
    <div className={styles.venmoButtonWrapper}>
      {!allValid && <div className={styles.buttonDisableLayer} />}
      {!venmo && <Spinner />}
      {venmo && (
        <button
          aria-label="Venmo"
          className={styles.venmoButton}
          disabled={disabled}
          onClick={() => {
            setDisabled(true)
            venmo?.tokenize((tokenizeErr, payload) => {
              if (typeof document !== 'undefined') {
                const backdrop = document.getElementById('venmo-desktop-web-backdrop')
                if (backdrop) {
                  backdrop.style.position = 'fixed'
                }
              }
              setDisabled(false)
              if (tokenizeErr) {
                return
              }
              if (payload) {
                submitPayment({
                  deviceData,
                  formData: payload,
                  isValid: true,
                  type: option.type,
                }, response => handleBraintreePaymentSubmitResponse(option.type, response))
              }
            })
          }}
          type="button"
        >
          <PaymentIcon name="venmo" url={option.logo?.url || ''} />
        </button>
      )}
    </div>
  )
}
